import { clientApi } from "@/redux/api";

import {
  IMAGE_MAX_BYTES,
  IMAGE_MIME_TYPES,
  MEDIA_PRESIGN_URL,
  MediaKind,
} from "./constants";
import { MEDIA_ERROR_CODES } from "./errors";

const SUPPORTED_TYPES = new Set<string>(IMAGE_MIME_TYPES);

export class MediaUploadError extends Error {
  code: string;
  constructor(code: string) {
    super(code);
    this.code = code;
  }
}

type PresignResponse = {
  id: string;
  uploadUrl: string;
  fields: Record<string, string>;
};

const extractApiErrorCode = (err: unknown, fallbackCode: string): string => {
  const e = err as {
    response?: { data?: { errors?: Array<{ code?: string }> } };
  };
  return e?.response?.data?.errors?.[0]?.code || fallbackCode;
};

export const uploadMedia = async (
  file: File,
  kind: MediaKind,
): Promise<{ id: string }> => {
  if (!SUPPORTED_TYPES.has(file.type)) {
    throw new MediaUploadError(MEDIA_ERROR_CODES.UNSUPPORTED_CONTENT_TYPE);
  }
  if (file.size > IMAGE_MAX_BYTES) {
    throw new MediaUploadError(MEDIA_ERROR_CODES.SIZE_OUT_OF_RANGE);
  }

  let presign: PresignResponse;
  try {
    const response = await clientApi.post<PresignResponse>(MEDIA_PRESIGN_URL, {
      kind,
      contentType: file.type,
      size: file.size,
      filename: file.name,
    });
    presign = response.data;
  } catch (err) {
    throw new MediaUploadError(
      extractApiErrorCode(err, MEDIA_ERROR_CODES.CLIENT_PRESIGN_FAILED),
    );
  }

  const formData = new FormData();
  Object.entries(presign.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  let s3Response: Response;
  try {
    s3Response = await fetch(presign.uploadUrl, {
      method: "POST",
      body: formData,
    });
  } catch {
    throw new MediaUploadError(MEDIA_ERROR_CODES.CLIENT_S3_NETWORK);
  }

  if (!s3Response.ok) {
    throw new MediaUploadError(MEDIA_ERROR_CODES.CLIENT_S3_FAILED);
  }

  return { id: presign.id };
};
